import React, { useMemo } from "react";
import ResponsiveRows from "../../components/ResponsiveRows";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  FiArrowUpRight,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiCopy,
} from "react-icons/fi";

const formatCreatedDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const PaginatedList = ({
  links,
  loading,
  pageIndex,
  pageSize,
  rowCount,
  onPaginationChange,
  onCopy,
  onOpen,
  selectedCodes = new Set(),
  onToggleSelect,
  onToggleSelectAll,
}) => {
  const allPageSelected = useMemo(() => {
    if (!links || links.length === 0) return false;
    return links.every((l) => selectedCodes.has(l.short_code));
  }, [links, selectedCodes]);

  const columns = useMemo(() => {
    const cols = [];

    if (onToggleSelect) {
      cols.push({
        id: "select",
        header: () => (
          <input
            type="checkbox"
            className="form-check-input mt-0"
            checked={allPageSelected}
            onChange={onToggleSelectAll}
            aria-label="Select all links on this page"
          />
        ),
        cell: ({ row }) => {
          const code = row.original.short_code;
          const isSelected = selectedCodes.has(code);
          return (
            <input
              type="checkbox"
              className="form-check-input mt-0"
              checked={isSelected}
              onChange={() => onToggleSelect(code)}
              aria-label={`Select ${code}`}
            />
          );
        },
      });
    }

    cols.push(
      {
        accessorKey: "short_code",
        header: "Short code",
        cell: ({ row }) => (
          <button type="button" className="shortlinks-code" onClick={() => onOpen(row.original.short_code)}>
            {row.original.short_code || "-"}<FiArrowUpRight aria-hidden="true" />
          </button>
        ),
      },
      {
        accessorKey: "url",
        header: "Destination",
        cell: ({ row }) => (
          <a className="shortlinks-destination" href={row.original.url} target="_blank" rel="noreferrer" title={row.original.url}>
            {row.original.url || "-"}
          </a>
        ),
      },
      { accessorKey: "title", header: "Title", cell: ({ getValue }) => getValue() || "Untitled" },
      { accessorKey: "clientName", header: "Client", cell: ({ getValue }) => getValue() || "-" },
      { accessorKey: "clicks_count", header: "Clicks", cell: ({ getValue }) => Number(getValue() || 0).toLocaleString() },
      { accessorKey: "createdAt", header: "Created", cell: ({ getValue }) => formatCreatedDate(getValue()) },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <button
            type="button"
            className="shortlinks-copy"
            onClick={() => onCopy(row.original.short_code)}
            aria-label={`Copy short link ${row.original.short_code}`}
            title="Copy short link"
          >
            <FiCopy aria-hidden="true" />
          </button>
        ),
      }
    );

    return cols;
  }, [allPageSelected, onCopy, onOpen, onToggleSelect, onToggleSelectAll, selectedCodes]);

  const table = useReactTable({
    data: links,
    columns,
    state: { pagination: { pageIndex, pageSize } },
    onPaginationChange,
    manualPagination: true,
    rowCount,
    getRowId: (row) => row._id || row.short_code,
    getCoreRowModel: getCoreRowModel(),
  });
  const rows = table.getRowModel().rows;
  const firstRow = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, rowCount);

  return (
    <div className="shortlinks-table-shell">
      <ResponsiveRows
        rows={loading ? [] : rows}
        getRowId={(row) => row.id}
        resetKey={`${pageIndex}-${pageSize}`}
        renderSummary={({ original: link }) => <><strong>{link.short_code} · {link.title || "Untitled"}</strong><span>{Number(link.clicks_count || 0).toLocaleString()} clicks</span></>}
        renderDetails={(row) => (
          <dl>{row.getVisibleCells().map((cell) => (
            <div key={cell.id}>
              <dt>{cell.column.columnDef.header || "Actions"}</dt>
              <dd>{flexRender(cell.column.columnDef.cell, cell.getContext())}</dd>
            </div>
          ))}</dl>
        )}
      >
      <div className="shortlinks-table-scroll">
        <table className="shortlinks-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} scope="col">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="shortlinks-table-message" colSpan={columns.length} role="status">Loading short links...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td className="shortlinks-table-message" colSpan={columns.length}>No short links found.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ResponsiveRows>

      <footer className="shortlinks-pagination">
        <div className="shortlinks-pagination-summary">
          <label htmlFor="shortlinks-page-size">Rows per page</label>
          <select id="shortlinks-page-size" value={pageSize} onChange={(event) => table.setPageSize(Number(event.target.value))}>
            {[10, 12, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
          <span className="shortlinks-row-range">{firstRow}-{lastRow} of {rowCount}</span>
        </div>
        <div className="shortlinks-page-controls" aria-label="Table pagination">
          <button type="button" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} aria-label="First page"><FiChevronsLeft /></button>
          <button type="button" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} aria-label="Previous page"><FiChevronLeft /></button>
          <span>Page {pageIndex + 1} of {Math.max(table.getPageCount(), 1)}</span>
          <button type="button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} aria-label="Next page"><FiChevronRight /></button>
          <button type="button" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} aria-label="Last page"><FiChevronsRight /></button>
        </div>
      </footer>
    </div>
  );
};

export default PaginatedList;